import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { ListenerBadge, LISTENER_TIER_CONFIG } from './ListenerBadge';
import { Headphones, Zap, Flame, Gem, Crown, TrendingUp } from 'lucide-react';

/**
 * Listener XP progress bar — shows level, XP, tier, and progress to next level.
 * Used on listener dashboard and profile pages.
 */
export function ListenerXPBar({ profile: externalProfile }) {
  const { getAuthHeaders } = useAuth();
  const [profile, setProfile] = useState(externalProfile || null);
  const [loading, setLoading] = useState(!externalProfile);

  useEffect(() => {
    if (externalProfile) {
      setProfile(externalProfile);
      return;
    }
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API}/listener/profile`, { headers: getAuthHeaders() });
        if (res.ok) setProfile(await res.json());
      } catch {} finally { setLoading(false); }
    };
    fetchProfile();
  }, [externalProfile, getAuthHeaders]);

  if (loading || !profile) return null;

  const { listener_level, listener_tier, tier_label, tier_color, progress } = profile;
  const cfg = LISTENER_TIER_CONFIG[listener_tier] || LISTENER_TIER_CONFIG.free;
  const Icon = cfg.Icon;

  return (
    <div className="rounded-xl border border-border/40 bg-card/50 p-4" data-testid="listener-xp-bar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tier_color}20` }}>
            <Icon className="w-4 h-4" style={{ color: tier_color }} />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">Level {listener_level}</p>
            <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: tier_color }}>{tier_label}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-mono font-bold text-foreground">{profile.listener_xp?.toLocaleString()} XP</p>
          <p className="text-[10px] text-muted-foreground">{progress?.xp_to_next?.toLocaleString()} to next</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 rounded-full bg-muted/30 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${progress?.progress_percent || 0}%`, background: tier_color }}
        />
      </div>

      {/* Quick stats row */}
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> Score: {profile.engagement_score || 0}
        </span>
        <span>Badges: {profile.badges_earned}/{profile.badges_total}</span>
        {profile.login_streak > 0 && (
          <span className="flex items-center gap-1 text-orange-400">
            <Flame className="w-3 h-3" /> {profile.login_streak}d streak
          </span>
        )}
      </div>
    </div>
  );
}
